import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
  Modal,
  InputLabel,
  Grid,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  AlertTitle,
  Backdrop,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { Theme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import { useLocalStorage } from "../../utils/LocalStorageHook";
import TableAbstraction from "../../componentUtils/TableAbstraction";
import { checkForRole } from "../../utils/GetAuth";
import LockIcon from "@mui/icons-material/Lock";
import Import from "@mui/icons-material/ImportExport";
import Export from "@mui/icons-material/FileDownloadOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import Clear from "@mui/icons-material/Clear";

import {
  getSeasonality,
  saveImportedSeasonality,
  editSeasonality,
  deleteSeasonality,
} from "../../utils/AdminSeasonality";
import Papa from "papaparse";

interface alertFields {
  alertTitle: any;
  alertMessage: any;
  isAlertOpen: boolean;
}
interface dataValidationAlertFields {
  alertTitle: any;
  data: any;
  isAlertOpen: boolean;
}
function getModalStyle() {
  const top = 50;
  const left = 50;

  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerDiv: {
      textAlign: "center",
    },
    root: {},
    divWidth: {
      width: "100%",
    },
    modal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    modalGrid: {
      marginTop: theme.spacing(2),
    },
    modalTitle: {
      color: theme.palette.primary.main,
    },
    modalCard: {},
    modalText: {
      width: 800,
    },
    modalInput: {
      paddingLeft: "2px",
      paddingRight: "20px",
      width: "200px",
    },
    paper: {
      position: "absolute",
      backgroundColor: theme.palette.background.paper,
      border: `1px solid`,
      borderColor: theme.palette.border.main,
      boxShadow: theme.shadows[4],
      padding: theme.spacing(2, 2, 2),
    },
    importLabel: {
      color: "#e9232a",
      fontFamily: "Roboto",
      fontSize: "0.8125rem",
      fontWeight: 500,
      letterSpacing: "0.02857em",
    },
    tableRow: {
      "&$hover:hover": {
        backgroundColor: "gray",
      },
    },
    hover: {},
  })
);

const seasonalityMonths: string[] = [
  "JANUARY(%)",
  "FEBRUARY(%)",
  "MARCH(%)",
  "APRIL(%)",
  "MAY(%)",
  "JUNE(%)",
  "JULY(%)",
  "AUGUST(%)",
  "SEPTEMBER(%)",
  "OCTOBER(%)",
  "NOVEMBER(%)",
  "DECEMBER(%)",
];

/**
 * Seasonality Configuration Functionality
 * @returns
 */
export default function AdminSeasonalityConfig() {
  const classes = useStyles();
  const [rows, SetRows] = useState<any[]>([]);
  const [salesOrg] = useLocalStorage("SETTINGS.salesOrg", "");
  const [clientId] = useLocalStorage("SETTINGS.clientId", 100);
  const enabled = checkForRole(`${clientId}`, salesOrg, "vs_admin");
  const [useCompact] = useLocalStorage("SETTINGS.compact", true);

  const [muiTable, setMuiTable] = React.useState<any>({ rpp: 25 });

  const [confirmDelete, setConfirmDelete] = React.useState<boolean>(false);
  const [confirmImport, setConfirmImport] = React.useState<boolean>(false);

  const [modalStyle] = React.useState(getModalStyle);
  const [openEditWindow, setOpenEditWindow] = React.useState(false);

  const initialSelectedOptions = {
    tradeChannelCode: null,
    subChannelCode: null,
    customerNum: null,
  };
  const [selectedOptions, setSelectedOptions] = React.useState<any>(initialSelectedOptions);
  const [displayOption, setDisplayOption] = React.useState<any>({});

  const [edit, setEdit] = React.useState<boolean>(false);
  const [backdropLoader, setBackdropLoader] = React.useState<boolean>(false);
  const [commonAlert, setCommonAlert] = useState<alertFields>({ alertTitle: "", alertMessage: "", isAlertOpen: false });
  const [dataValidationAlert, setDataValidationAlert] = useState<dataValidationAlertFields>({
    alertTitle: "",
    data: [],
    isAlertOpen: false,
  });
  const [csvFileData, setCsvFileData] = React.useState<any>({});

  const ModelBodyEditableFields: string[] = [
    "JANUARY",
    "FEBRUARY",
    "MARCH",
    "APRIL",
    "MAY",
    "JUNE",
    "JULY",
    "AUGUST",
    "SEPTEMBER",
    "OCTOBER",
    "NOVEMBER",
    "DECEMBER",
  ];

  const fileImportConfirmationMsg = `Existing seasonality configurations will be replaced with configurations from the uploaded file. Would you like to continue?`;

  const handleModalClose = () => {
    setOpenEditWindow(false);
    setEdit(false);
    setSelectedOptions(initialSelectedOptions);
  };

  const handleModalSave = () => {
    setBackdropLoader(true);
    editSeasonality(salesOrg, selectedOptions)
      .then((response: any) => {
        if (response === "S") {
          setEdit(false);
          setSelectedOptions(initialSelectedOptions);
          setTimeout(() => {
            getSeasonality(salesOrg.toString()).then((v: any) => {
              SetRows(v);
              setBackdropLoader(false);
            });
          }, 800);
        }
      })
      .catch((error: any) => {
        setBackdropLoader(false);
        setCommonAlert({
          alertTitle: "!Technical Error",
          alertMessage: "Failed to edit the record. ",
          isAlertOpen: true,
        });
      });
    setOpenEditWindow(false);
  };

  const handleDelete = () => {
    setBackdropLoader(true);
    deleteSeasonality(salesOrg, displayOption?.displayColumn, displayOption?.displayValue)
      .then((response: any) => {
        if (response === "S") {
          setEdit(false);
          setSelectedOptions(initialSelectedOptions);
          setTimeout(() => {
            getSeasonality(salesOrg.toString()).then((v: any) => {
              SetRows(v);
              setBackdropLoader(false);
            });
          }, 800);
        }
      })
      .catch((error: any) => {
        setCommonAlert({
          alertTitle: "!Technical Error",
          alertMessage: "Failed to delete the record. ",
          isAlertOpen: true,
        });
        setBackdropLoader(false);
      });
  };

  const handleFactorsOnChange = (value: any, key: string) => {
    const seasonVal = value && value != "-" ? parseInt(value) || 0 : value;
    if (seasonVal < -100 || seasonVal > 100) {
      return setCommonAlert({
        alertTitle: "!Validation Error",
        alertMessage: "Value must be between -100 to 100",
        isAlertOpen: true,
      });
    }
    selectedOptions[key] = seasonVal.toString();
    setSelectedOptions({ ...selectedOptions });
  };

  const setCurrent = (tableMeta: any) => {
    let displayObject: any = {};
    if (tableMeta.customer_num) {
      displayObject.displayLabel = "Customer Number";
      displayObject.displayColumn = "customer_num";
      displayObject.displayValue = tableMeta.customer_num;
      displayObject.displayLabelValue = tableMeta.customer_num;
    } else if (tableMeta.subtrade_channel) {
      displayObject.displayLabel = "Sub-Trade Channel";
      displayObject.displayColumn = "subtrade_channel";
      displayObject.displayValue = tableMeta.subtrade_channel;
      displayObject.displayLabelValue = `${tableMeta.subtrade_channel_name} (${tableMeta.subtrade_channel})`;
    } else if (tableMeta.trade_channel) {
      displayObject.displayLabel = "Trade Channel";
      displayObject.displayColumn = "trade_channel";
      displayObject.displayValue = tableMeta.trade_channel;
      displayObject.displayLabelValue = `${tableMeta.trade_channel_name} (${tableMeta.trade_channel})`;
    }
    ModelBodyEditableFields.map((key: any) => {
      tableMeta[key.toLowerCase().substring(0, 3)] = tableMeta[key.toLowerCase().substring(0, 3)].toString();
    });
    setDisplayOption({ ...displayObject });
    setSelectedOptions({ ...tableMeta, typeOf: displayObject.displayColumn });
  };

  const handleEdit = (tableMeta: any) => {
    setEdit(true);
    if (tableMeta) setCurrent(tableMeta);
    setOpenEditWindow(true);
  };

  const saveImportedCSV = (data: any) => {
    setBackdropLoader(true);
    saveImportedSeasonality(salesOrg, data)
      .then((response: any) => {
        if (response.status === "F") {
          const errorData: any = handleErrorFromAPI(csvFileData, response?.message);
          setDataValidationAlert({ alertTitle: "Validation Error", data: errorData, isAlertOpen: true });
          setBackdropLoader(false);
          return;
        }
        setTimeout(() => {
          getSeasonality(salesOrg.toString()).then((v: any) => {
            SetRows(v);
            setBackdropLoader(false);
          });
        }, 800);
      })
      .catch((error: any) => {
        setCommonAlert({
          alertTitle: "!Technical Error",
          alertMessage: "Failed to import the file. ",
          isAlertOpen: true,
        });
        setBackdropLoader(false);
        return;
      });
  };

  const mapCSVColumnsToDataModel = (data: any) => {
    const dataModel: any = {
      trade_channel: "Trade Channel",
      subtrade_channel: "Sub-Trade Channel",
      customer_num: "Customer Number",
      jan: "JANUARY(%)",
      feb: "FEBRUARY(%)",
      mar: "MARCH(%)",
      apr: "APRIL(%)",
      may: "MAY(%)",
      jun: "JUNE(%)",
      jul: "JULY(%)",
      aug: "AUGUST(%)",
      sept: "SEPTEMBER(%)",
      oct: "OCTOBER(%)",
      nov: "NOVEMBER(%)",
      dec: "DECEMBER(%)",
    };
    const updatedData: any = [];
    data.forEach((ele: any) => {
      if (Object.keys(ele).length < 15) return;

      const mappedData: any = {};
      for (const key in dataModel) {
        if (Object.prototype.hasOwnProperty.call(dataModel, key)) {
          mappedData[key] = ele[dataModel[key]] || "";
        }
      }
      return updatedData.push(mappedData);
    });

    return updatedData;
  };

  const validateCSVFile = (fileName: string, csvData: any) => {
    let isValid = false;
    const regex = /\.csv$|\.xls(x)?$/i;
    const preDefinedColumns = [
      "Trade Channel",
      "Sub-Trade Channel",
      "Customer Number",
      "JANUARY(%)",
      "FEBRUARY(%)",
      "MARCH(%)",
      "APRIL(%)",
      "MAY(%)",
      "JUNE(%)",
      "JULY(%)",
      "AUGUST(%)",
      "SEPTEMBER(%)",
      "OCTOBER(%)",
      "NOVEMBER(%)",
      "DECEMBER(%)",
    ];
    const { meta } = csvData;
    //1. File validation, .cdv, .xlsm .xlxs are valid files.
    if (!fileName || !regex.test(fileName)) {
      return { isValid, alertMessage: `Invalid File, Only (.csv |.xlx |.xlxs) are allowed to import.` };
    }

    //2. Missing or if coulmns are modified by user validation
    var missingColumns = preDefinedColumns.filter((e) => !meta?.fields.includes(e));
    if (missingColumns.length) {
      return { isValid, alertMessage: `Missing Column "${missingColumns}", Please add to the file.` };
    }
    //When passes every checks above sucessfully
    return { isValid: true, alertMessage: "" };
  };

  const validateCSVData = (csvData: any) => {
    let isValid = false;
    //3. Check if trade channel, Sub-Trade Channel Code and customer number is duplicate.
    const duplicates: any = [];
    const incorrectFactorRecords: any = [];
    const { data } = csvData;
    data.forEach((ele: any, index: number, self: any) => {
      delete ele["Trade Channel Name"];
      delete ele["Sub-Trade Channel Name"];
      const keys = Object.keys(ele);
      if (Object.keys(ele).length < 15) return;

      for (let i = 0; i <= keys.length; i++) {
        if (keys[i] !== "Trade Channel" && keys[i] !== "Sub-Trade Channel" && keys[i] !== "Customer Number") {
          if (ele[keys[i]] && isNaN(ele[keys[i]])) {
            ele["reason"] = "Incorrect Seasonality Factor";
            incorrectFactorRecords.push(ele);
          }
        }
      }
      //Triming the values
      ele["Trade Channel"] = ele["Trade Channel"].trim();
      ele["Sub-Trade Channel"] = ele["Sub-Trade Channel"].trim();
      ele["Customer Number"] = ele["Customer Number"].trim();

      if (ele["Trade Channel"] && ele["Sub-Trade Channel"]) {
        if (
          self.findIndex(
            (item: any) =>
              item["Trade Channel"] === ele["Trade Channel"] && item["Sub-Trade Channel"] === ele["Sub-Trade Channel"]
          ) !== index
        ) {
          const found = duplicates.find(
            (o: any) =>
              o["Trade Channel"] === ele["Trade Channel"] && o["Sub-Trade Channel"] === ele["Sub-Trade Channel"]
          );
          if (!found) {
            ele["reason"] = "Duplicate: Trade & Sub-Trade Channel";
            duplicates.push(ele);
          }
        }
      }
      if (ele["Trade Channel"] && !ele["Sub-Trade Channel"]) {
        if (
          self.findIndex(
            (item: any) =>
              item["Trade Channel"] === ele["Trade Channel"] && ele["Sub-Trade Channel"] === item["Sub-Trade Channel"]
          ) !== index
        ) {
          const found = duplicates.find(
            (o: any) =>
              o["Trade Channel"] === ele["Trade Channel"] && o["Sub-Trade Channel"] === ele["Sub-Trade Channel"]
          );
          if (!found) {
            ele["reason"] = "Duplicate: Trade Channel";
            duplicates.push(ele);
          }
        }
      }
      if (ele["Customer Number"]) {
        if (self.findIndex((item: any) => item["Customer Number"] === ele["Customer Number"]) !== index) {
          const found = duplicates.find((o: any) => o["Customer Number"] === ele["Customer Number"]);
          if (!found) {
            ele["reason"] = "Duplicate: Customer Number";
            duplicates.push(ele);
          }
        }
      }
      if (ele["Sub-Trade Channel"] && !ele["Trade Channel"]) {
        if (
          self.findIndex(
            (item: any) =>
              item["Sub-Trade Channel"] === ele["Sub-Trade Channel"] && item["Trade Channel"] === ele["Trade Channel"]
          ) !== index
        ) {
          const found = duplicates.find(
            (o: any) =>
              o["Sub-Trade Channel"] === ele["Sub-Trade Channel"] && o["Trade Channel"] === ele["Trade Channel"]
          );
          if (!found) {
            ele["reason"] = "Duplicate: Sub-Trade Channel";
            duplicates.push(ele);
          }
        } else {
          ele["reason"] = "Required: Trade Channel";
          duplicates.push(ele);
        }
      }
      if (!Number(ele["Trade Channel"]) && !Number(ele["Sub-Trade Channel"]) && !Number(ele["Customer Number"])) {
        ele["reason"] = "Required: Trade Channel OR Customer Number OR (Sub-Trade AND Trade Channel)";
        duplicates.push(ele);
      }
    });
    return { duplicates, incorrectFactorRecords };
  };

  const handleErrorFromAPI = (data: any, errorData: any) => {
    const filteredDataWithError: any = [];
    errorData.forEach((item: any) => {
      let keyToSearch: string = "";
      let reason: string = "";
      if (item.status === "customer_num") {
        keyToSearch = "Customer Number";
        reason = "Invalid Customer Number";
      } else if (item.status === "subtrade_channel") {
        keyToSearch = "Sub-Trade Channel";
        reason = "Invalid Sub-Trade Channel";
      } else if (item.status === "trade_name") {
        keyToSearch = "Trade Channel";
        reason = "Invalid Trade Channel";
      }
      item?.message.forEach((ele: any) => {
        const foundItem = data.find((odata: any) => {
          const dataToSearch =
            odata[keyToSearch] && keyToSearch === "Customer Number" && odata[keyToSearch][0] !== "0"
              ? "0" + odata[keyToSearch]
              : odata[keyToSearch];
          if (dataToSearch === ele.value) return odata;

          return undefined;
        });
        if (foundItem) {
          foundItem["reason"] = reason;
          filteredDataWithError.push(foundItem);
        }
      });
    });
    return filteredDataWithError;
  };

  const handleImport = () => {
    const updatedData = mapCSVColumnsToDataModel(csvFileData);
    saveImportedCSV(updatedData);
  };

  const processImportedCSV = (results: any, fileName: string) => {
    const alertErrorTitle = "!Validation Error.";
    const csvDataResult: any = { meta: results?.meta, data: results?.data };

    if (csvDataResult.data.length) {
      const { isValid, alertMessage } = validateCSVFile(fileName, csvDataResult);
      if (!isValid) {
        setCommonAlert({ alertTitle: alertErrorTitle, alertMessage: alertMessage, isAlertOpen: true });
        setBackdropLoader(false);
        return;
      }
      const { duplicates, incorrectFactorRecords }: any = validateCSVData(csvDataResult);
      if (duplicates.length) {
        setDataValidationAlert({ alertTitle: alertErrorTitle, data: duplicates, isAlertOpen: true });
        setBackdropLoader(false);
        return;
      }
      if (incorrectFactorRecords.length) {
        setDataValidationAlert({ alertTitle: alertErrorTitle, data: incorrectFactorRecords, isAlertOpen: true });
        setBackdropLoader(false);
        return;
      }
    }
    setCsvFileData(csvDataResult.data);
    setConfirmImport(true);
  };

  const handleFileChange = (e: any) => {
    setBackdropLoader(true);
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (event: any) => {
      Papa.parse(event.target.result, {
        header: true,
        skipEmptyLines: "greedy",
        delimiter: ",", // auto-detect
        complete: (results: any) => {
          processImportedCSV(results, file.name);
        },
      });
    };

    reader.readAsText(file);
  };

  const modalBody = (
    <div style={modalStyle} className={classes.paper}>
      <Typography variant="h4" className={classes.modalTitle} paragraph>
        Edit Seasonality
      </Typography>
      <Grid container spacing={2} className={classes.modalGrid}>
        <Grid item xs={14} md={14} xl={12}>
          <Card className={classes.modalCard}>
            <CardContent>
              <Grid container rowSpacing={2} className={classes.modalGrid}>
                <Grid item xs={12} md={12} xl={10}>
                  <InputLabel>{displayOption?.displayLabel}</InputLabel>
                  <TextField
                    title={displayOption?.displayLabelValue}
                    placeholder={displayOption?.displayLabel}
                    value={displayOption?.displayLabelValue}
                    className={classes.modalInput}
                    disabled={edit}
                  ></TextField>
                </Grid>
                {ModelBodyEditableFields.map((ele: any) => {
                  return (
                    <Grid item xs={6} md={6} xl={4}>
                      <InputLabel>{ele}</InputLabel>
                      <TextField
                        helperText={"Enter value from -100 to 100"}
                        placeholder="Enter Seasonality Factor"
                        type="number"
                        className={classes.modalInput}
                        value={selectedOptions[ele.toLowerCase().substring(0, 3)]}
                        onChange={(e) => handleFactorsOnChange(e.target.value, ele.toLowerCase().substring(0, 3))}
                      ></TextField>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
          <div style={{ paddingRight: "5px", textAlign: "right" }}>
            <React.Fragment>
              <Tooltip title={"Save"}>
                <IconButton onClick={handleModalSave} color="primary">
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={"Cancel"}>
                <IconButton onClick={handleModalClose} color="primary">
                  <Clear />
                </IconButton>
              </Tooltip>
            </React.Fragment>
          </div>
        </Grid>
      </Grid>
    </div>
  );

  useEffect(() => {
    setBackdropLoader(true);
    getSeasonality(salesOrg.toString()).then((v: any) => {
      SetRows(v);
      setBackdropLoader(false);
    });
  }, []);

  return (
    <div className={classes.root}>
      {enabled ? (
        <TableAbstraction
          settingsKey="ADM_BRC"
          options={{
            draggableColumns: {
              enabled: true,
            },
            rowsPerPageOptions: [25, 50, 100],
            serverSide: false,
            selectableRows: "none",
            rowsPerPage: muiTable.rpp,
            onChangeRowsPerPage: (numberOfRows) => {
              muiTable.rpp = numberOfRows;
              setMuiTable(muiTable);
            },
            customToolbar: () => {
              return (
                <Tooltip title={`Import Seasonality`}>
                  <Button component="label" style={{ marginRight: 10 }}>
                    <input
                      type="file"
                      accept=".csv, .xls, .xlsx"
                      hidden
                      name="seasonality"
                      id="seasonalityFile"
                      onChange={(e) => {
                        handleFileChange(e);
                      }}
                    />
                    <Import />
                    <div className={classes.importLabel}>IMPORT</div>
                  </Button>
                </Tooltip>
              );
            },
          }}
          title="Configure Seasonality"
          columns={[
            {
              label: "Trade Channel",
              name: "trade_channel",
              options: {
                searchable: true,
                filter: true,
                renderCell: (params) => {
                  const obj = params.row;
                  return `${
                    obj.trade_channel && obj.trade_channel_name
                      ? obj.trade_channel_name + "(" + obj.trade_channel + ")"
                      : obj.trade_channel || ""
                  }`;
                },
              },
            },
            {
              label: "Trade Channel Name",
              name: "trade_channel_name",
              options: {
                filter: false,
                display: false,
              },
            },
            {
              label: "Sub-Trade Channel",
              name: "subtrade_channel",
              options: {
                searchable: true,
                filter: true,
                renderCell: (params) => {
                  const obj = params.row;
                  return `${
                    obj.subtrade_channel && obj.subtrade_channel_name
                      ? obj.subtrade_channel_name + "(" + obj.subtrade_channel + ")"
                      : obj.subtrade_channel || ""
                  }`;
                },
              },
            },
            {
              label: "Sub-Trade Channel Name",
              name: "subtrade_channel_name",
              options: {
                filter: false,
                display: false,
              },
            },
            {
              label: "Customer Number",
              name: "customer_num",
              options: {
                searchable: true,
                filter: true,
                renderCell: (params) => {
                  const obj = params.row;
                  return `${obj.customer_num || ""}`;
                },
              },
            },
            {
              label: "Customer Name",
              name: "customer_name",
              options: {
                searchable: false,
                filter: false,
                disableExport: true,
              },
            },
            {
              label: "Customer Address",
              name: "address",
              options: {
                searchable: false,
                filter: false,
                disableExport: true,
              },
            },
            {
              label: "Equipment Number",
              name: "equipment_num",
              options: {
                searchable: false,
                filter: false,
                disableExport: true,
              },
            },
            {
              label: "Equipment Location",
              name: "equipment_location",
              options: {
                searchable: false,
                filter: false,
                disableExport: true,
              },
            },
            {
              label: "JANUARY(%)",
              name: "jan",
              options: {
                searchable: false,
                filter: false,
              },
            },
            {
              label: "FEBRUARY(%)",
              name: "feb",
              options: {
                searchable: false,
                filter: false,
              },
            },
            {
              label: "MARCH(%)",
              name: "mar",
              options: {
                searchable: false,
                filter: false,
              },
            },
            {
              label: "APRIL(%)",
              name: "apr",
              options: {
                searchable: false,
                filter: false,
              },
            },
            {
              label: "MAY(%)",
              name: "may",
              options: {
                searchable: false,
                filter: false,
              },
            },
            {
              label: "JUNE(%)",
              name: "jun",
              options: {
                searchable: false,
                filter: false,
              },
            },
            {
              label: "JULY(%)",
              name: "jul",
              options: {
                searchable: false,
                filter: false,
              },
            },
            {
              label: "AUGUST(%)",
              name: "aug",
              options: {
                searchable: false,
                filter: false,
              },
            },
            {
              label: "SEPTEMBER(%)",
              name: "sep",
              options: {
                searchable: false,
                filter: false,
              },
            },
            {
              label: "OCTOBER(%)",
              name: "oct",
              options: {
                searchable: false,
                filter: false,
              },
            },
            {
              label: "NOVEMBER(%)",
              name: "nov",
              options: {
                searchable: false,
                filter: false,
              },
            },
            {
              label: "DECEMBER(%)",
              name: "dec",
              options: {
                searchable: false,
                filter: false,
              },
            },
            {
              label: "Action",
              name: "remove",
              disableReorder: true,
              options: {
                filter: false,
                setCellProps: () => ({ align: "right" }),
                renderCell: (props) => {
                  const tableMeta = props.row;
                  if (useCompact)
                    return (
                      <React.Fragment>
                        <Tooltip title={"Edit"}>
                          <EditIcon
                            fontSize="small"
                            onClick={() => {
                              handleEdit(tableMeta);
                            }}
                          />
                        </Tooltip>
                        <Tooltip title={"Delete"}>
                          <DeleteIcon
                            fontSize="small"
                            onClick={() => {
                              setCurrent(tableMeta);
                              setConfirmDelete(true);
                            }}
                          />
                        </Tooltip>
                      </React.Fragment>
                    );
                  else
                    return (
                      <React.Fragment>
                        <Tooltip title={"Edit"}>
                          <IconButton
                            onClick={() => {
                              handleEdit(tableMeta);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={"Delete"}>
                          <IconButton
                            onClick={() => {
                              setCurrent(tableMeta);
                              setConfirmDelete(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </React.Fragment>
                    );
                },
                disableExport: true,
              },
            },
          ]}
          data={rows}
          noRowMessage="No Rows, Please use Export to download template then Import to populate data."
          exportFileName="Configure Seasonality"
          exportHiddenColumn={true}
        />
      ) : enabled ? (
        <div className={classes.centerDiv}>
          <CircularProgress color="primary" />
        </div>
      ) : (
        <Alert severity="error">
          <AlertTitle>
            <LockIcon /> Access Error
          </AlertTitle>
          You do not currently have access to <strong>Vend Smart Admin</strong> activities for the{" "}
          <strong>{salesOrg}</strong> bottler.
        </Alert>
      )}

      <Modal open={openEditWindow} onClose={handleModalClose}>
        {modalBody}
      </Modal>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you wish to delete ?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleDelete();
              setConfirmDelete(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button
            onClick={() => {
              // setEditedRow(undefined);
              setConfirmDelete(false);
            }}
            color="primary"
            autoFocus
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmImport} onClose={() => setConfirmImport(false)}>
        <DialogTitle>Confirm Import</DialogTitle>
        <DialogContent>
          <DialogContentText>{fileImportConfirmationMsg}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleImport();
              setConfirmImport(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button
            onClick={() => {
              setConfirmImport(false);
              setBackdropLoader(false);
            }}
            color="primary"
            autoFocus
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={commonAlert.isAlertOpen} onClose={() => setCommonAlert({ ...commonAlert, isAlertOpen: false })}>
        <DialogTitle>{commonAlert.alertTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div style={{ whiteSpace: "pre-line" }}>{commonAlert.alertMessage}</div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCommonAlert({ ...commonAlert, isAlertOpen: false });
            }}
            color="primary"
            autoFocus
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={dataValidationAlert.isAlertOpen}
        onClose={() => setDataValidationAlert({ ...dataValidationAlert, isAlertOpen: false })}
      >
        <DialogTitle>{dataValidationAlert.alertTitle}</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="right">Reason</TableCell>
                  <TableCell align="right">Trade Channel</TableCell>
                  <TableCell align="right">Sub-Trade Channel</TableCell>
                  <TableCell align="right">Customer Number</TableCell>
                  <TableCell align="right">JANUARY</TableCell>
                  <TableCell align="right">FEBRAUARY</TableCell>
                  <TableCell align="right">MARCH</TableCell>
                  <TableCell align="right">APRIL</TableCell>
                  <TableCell align="right">MAY</TableCell>
                  <TableCell align="right">JUNE</TableCell>
                  <TableCell align="right">JULY</TableCell>
                  <TableCell align="right">AUGUST</TableCell>
                  <TableCell align="right">SEPTEMBER</TableCell>
                  <TableCell align="right">OCTOBER</TableCell>
                  <TableCell align="right">NOVEMBER</TableCell>
                  <TableCell align="right">DECEMBER</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataValidationAlert.data.map((ele: any, index: number) => {
                  return (
                    <TableRow key={index} hover classes={{ hover: classes.hover }} className={classes.tableRow}>
                      <TableCell align="right">{`${ele["reason"] || ""}`}</TableCell>
                      <TableCell align="right">{`${ele["Trade Channel"] || ""}`}</TableCell>
                      <TableCell align="right">{`${ele["Sub-Trade Channel"] || ""}`}</TableCell>
                      <TableCell align="right">{`${ele["Customer Number"] || ""}`}</TableCell>
                      {seasonalityMonths.map((key: any) => {
                        return (
                          <TableCell
                            style={{ backgroundColor: isNaN(ele[key]) ? "red" : "white" }}
                            title="Integers Allowed Only"
                            align="right"
                          >
                            {ele[key] || ""}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDataValidationAlert({ ...dataValidationAlert, isAlertOpen: false });
            }}
            color="primary"
            autoFocus
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      {/* backdrop loader */}
      <Backdrop sx={{ color: "#fff", zIndex: (theme: any) => theme.zIndex.drawer + 1 }} open={backdropLoader}>
        <CircularProgress color="primary" />
      </Backdrop>
    </div>
  );
}
